/*
 * THESIS: booking is picking a real slot on the firm's own calendar, not a
 *   third-party widget in an iframe; the page speaks the site's grammar.
 * OWN-WORLD: Maestro surfaces; cyan carries the calendar, lime confirms.
 * STORY: a hot lead lands, sees days and times in their own timezone, gives
 *   name and email, and leaves with a Meet invite on the calendar.
 * FIRST VIEWPORT: kicker, headline, and the first days of slots.
 * FORM: day columns of slot chips + one short form; graceful offline state
 *   pointing at /talk when the integration is not configured.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Seo from '../Seo'
import SiteAmbient from '../common/SiteAmbient'
import trackEvent from '../../utils/trackEvent'
import { getBookingContent } from '../../content/bookingContent'

const CONTACT_API = (process.env.REACT_APP_API_BASE_URL || 'https://app.leanit101.com')
  .replace(/\/$/, '')
  // plain http to the API dies in CORS preflight (nginx 301s to https)
  .replace(/^http:\/\/(?=[^/]*leanit101\.com)/, 'https://')

const isPrerender = () =>
  typeof navigator !== 'undefined' && /ReactSnap/.test(navigator.userAgent || '')

const BookingPage = () => {
  const { i18n } = useTranslation()
  const c = getBookingContent(i18n.language)
  const tz = useMemo(
    () => (typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'UTC'),
    []
  )

  const [phase, setPhase] = useState('loading') // loading | ready | unavailable | done
  const [slots, setSlots] = useState([])
  const [selected, setSelected] = useState(null)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [company, setCompany] = useState('')
  const [notes, setNotes] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const [confirmation, setConfirmation] = useState(null)
  const viewTracked = useRef(false)

  const load = useCallback(async () => {
    setPhase('loading')
    setSelected(null)
    try {
      const res = await fetch(
        `${CONTACT_API}/api/booking/availability?tz=${encodeURIComponent(tz)}&locale=${encodeURIComponent(i18n.language || 'en-US')}`
      )
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      if (!data.configured || !Array.isArray(data.slots) || data.slots.length === 0) {
        setPhase('unavailable')
        return
      }
      setSlots(data.slots)
      setPhase('ready')
    } catch (err) {
      console.error('[booking] availability failed', err)
      setPhase('unavailable')
    }
  }, [tz, i18n.language])

  useEffect(() => {
    if (isPrerender()) return
    load()
    if (!viewTracked.current) {
      viewTracked.current = true
      trackEvent('funnel_booking_view')
    }
  }, [load])

  // group slots into days in the visitor's timezone
  const days = useMemo(() => {
    const locale = i18n.language || 'en-US'
    const dayFmt = new Intl.DateTimeFormat(locale, { timeZone: tz, weekday: 'long', day: 'numeric', month: 'long' })
    const timeFmt = new Intl.DateTimeFormat(locale, { timeZone: tz, hour: '2-digit', minute: '2-digit' })
    const map = new Map()
    slots.forEach((slot) => {
      const start = new Date(slot.start)
      const key = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(start)
      if (!map.has(key)) map.set(key, { key, label: dayFmt.format(start), slots: [] })
      map.get(key).slots.push({ ...slot, timeLabel: timeFmt.format(start) })
    })
    return [...map.values()].sort((a, b) => (a.key < b.key ? -1 : 1))
  }, [slots, tz, i18n.language])

  // the month calendar: the visitor picks a day, then one of its openings
  const daysMap = useMemo(() => new Map(days.map((d) => [d.key, d])), [days])
  const monthsAvailable = useMemo(() => {
    const set = new Set(days.map((d) => d.key.slice(0, 7)))
    return [...set].sort()
  }, [days])
  const [viewMonth, setViewMonth] = useState(null)
  const [selectedDayKey, setSelectedDayKey] = useState(null)

  useEffect(() => {
    if (days.length === 0) return
    if (!selectedDayKey || !daysMap.has(selectedDayKey)) {
      setSelectedDayKey(days[0].key)
      setViewMonth(days[0].key.slice(0, 7))
    } else if (!viewMonth) {
      setViewMonth(selectedDayKey.slice(0, 7))
    }
  }, [days, daysMap, selectedDayKey, viewMonth])

  const todayKey = useMemo(
    () => new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date()),
    [tz]
  )

  const weekdayHeads = useMemo(() => {
    const locale = i18n.language || 'en-US'
    const fmt = new Intl.DateTimeFormat(locale, { weekday: 'short' })
    // 2024-01-01 was a Monday; the grid runs Monday to Sunday
    return [0, 1, 2, 3, 4, 5, 6].map((i) => fmt.format(new Date(2024, 0, 1 + i)))
  }, [i18n.language])

  const monthTitle = useMemo(() => {
    if (!viewMonth) return ''
    const [y, m] = viewMonth.split('-').map(Number)
    const locale = i18n.language || 'en-US'
    return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(new Date(y, m - 1, 1))
  }, [viewMonth, i18n.language])

  const monthCells = useMemo(() => {
    if (!viewMonth) return []
    const [y, m] = viewMonth.split('-').map(Number)
    const lead = (new Date(y, m - 1, 1).getDay() + 6) % 7
    const daysInMonth = new Date(y, m, 0).getDate()
    const cells = []
    for (let i = 0; i < lead; i += 1) cells.push(null)
    for (let d = 1; d <= daysInMonth; d += 1) {
      const key = `${viewMonth}-${String(d).padStart(2, '0')}`
      cells.push({ d, key, has: daysMap.has(key) })
    }
    return cells
  }, [viewMonth, daysMap])

  const monthIdx = monthsAvailable.indexOf(viewMonth)
  const selectedDay = selectedDayKey ? daysMap.get(selectedDayKey) : null

  const submit = async (e) => {
    e.preventDefault()
    setError('')
    if (!name.trim() || !email.trim() || !selected) {
      setError(c.labels.form.required)
      return
    }
    setSending(true)
    try {
      const res = await fetch(`${CONTACT_API}/api/booking/reserve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slotId: selected.id,
          name: name.trim(),
          email: email.trim(),
          company: company.trim(),
          notes: notes.trim(),
          locale: i18n.language || 'en-US',
          timezone: tz,
        }),
      })
      if (res.status === 410) {
        setSending(false)
        setError(c.labels.form.expired)
        load()
        return
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      setConfirmation({ ...data, slot: selected })
      setPhase('done')
      trackEvent('funnel_booking_confirmed')

    } catch (err) {
      console.error('[booking] reserve failed', err)
      trackEvent('funnel_booking_error')
      setSending(false)
      setError(c.labels.form.error)
    }
  }

  const confirmedWhen = useMemo(() => {
    if (!confirmation) return ''
    const locale = i18n.language || 'en-US'
    const fmt = new Intl.DateTimeFormat(locale, {
      timeZone: tz, weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit',
    })
    return fmt.format(new Date(confirmation.start))
  }, [confirmation, tz, i18n.language])

  return (
    <div className="relative isolate bg-transparent font-Quicksand text-white">
      <SiteAmbient />
      <Seo title={c.seo.title} description={c.seo.description} path="/book" />

      <header className="px-6 pb-6 pt-32 md:px-16 md:pt-40">
        <div className="mx-auto max-w-6xl">
          <p className="text-xs uppercase tracking-[0.4em] text-pyramid-19/80">{c.hero.kicker}</p>
          <h1 className="mt-4 max-w-3xl text-4xl font-light leading-[1.05] md:text-6xl">{c.hero.headline}</h1>
          <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300">{c.hero.body}</p>
        </div>
      </header>

      <main className="px-6 pb-24 md:px-16">
        <div className="mx-auto max-w-6xl">
          {phase === 'loading' ? (
            <p className="text-slate-400">{c.labels.loading}</p>
          ) : null}

          {phase === 'unavailable' ? (
            <div className="max-w-xl rounded-2xl border border-white/10 bg-white/[0.02] p-8">
              <p className="leading-7 text-slate-300">{c.labels.unavailable}</p>
              <a href="/talk" className="btn-board btn-board-cyan mt-6">
                {c.labels.unavailableCta}
                <span aria-hidden="true">&#8594;</span>
              </a>
            </div>
          ) : null}

          {phase === 'ready' ? (
            <div className="grid items-start gap-10 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]">
              <section>
                <div className="flex items-baseline justify-between gap-4">
                  <p className="text-xs uppercase tracking-[0.3em] text-slate-400">{c.labels.timezoneNote}: {tz}</p>
                  <button type="button" onClick={load} className="text-sm text-slate-400 underline decoration-white/20 underline-offset-4 hover:text-slate-200">
                    {c.labels.refresh}
                  </button>
                </div>
                {/* the month, with the few days that hold an opening */}
                <div className="mt-5 rounded-2xl border border-white/[0.08] bg-white/[0.02] p-5 md:p-6">
                  <div className="flex items-center justify-between">
                    <p className="text-lg font-semibold capitalize text-white">{monthTitle}</p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        aria-label={c.labels.calendar.prev}
                        disabled={monthIdx <= 0}
                        onClick={() => setViewMonth(monthsAvailable[monthIdx - 1])}
                        className="rounded-lg border border-white/[0.12] px-3 py-1.5 text-slate-300 transition hover:border-white/30 disabled:cursor-not-allowed disabled:opacity-30"
                      >
                        &#8592;
                      </button>
                      <button
                        type="button"
                        aria-label={c.labels.calendar.next}
                        disabled={monthIdx < 0 || monthIdx >= monthsAvailable.length - 1}
                        onClick={() => setViewMonth(monthsAvailable[monthIdx + 1])}
                        className="rounded-lg border border-white/[0.12] px-3 py-1.5 text-slate-300 transition hover:border-white/30 disabled:cursor-not-allowed disabled:opacity-30"
                      >
                        &#8594;
                      </button>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-7 gap-1.5 text-center">
                    {weekdayHeads.map((w) => (
                      <p key={w} className="pb-1 text-[11px] uppercase tracking-wider text-slate-500">{w}</p>
                    ))}
                    {monthCells.map((cell, i) =>
                      cell === null ? (
                        <span key={`b${i}`} aria-hidden="true" />
                      ) : (
                        <button
                          key={cell.key}
                          type="button"
                          disabled={!cell.has}
                          aria-pressed={selectedDayKey === cell.key}
                          onClick={() => {
                            setSelectedDayKey(cell.key)
                            setSelected(null)
                            trackEvent('funnel_booking_day', { day: cell.key })
                          }}
                          className={`aspect-square rounded-lg text-sm transition-all duration-200 ${
                            selectedDayKey === cell.key
                              ? 'bg-pyramid-19 font-bold text-[#101418]'
                              : cell.has
                              ? 'border border-pyramid-19/40 font-semibold text-pyramid-19 hover:bg-pyramid-19/10'
                              : `text-slate-600 ${cell.key === todayKey ? 'border border-white/[0.15]' : ''}`
                          }`}
                        >
                          {cell.d}
                        </button>
                      )
                    )}
                  </div>
                </div>

                {/* the selected day's openings */}
                <div className="mt-5 rounded-2xl border border-white/[0.08] bg-white/[0.02] p-5">
                  {selectedDay ? (
                    <>
                      <p className="text-sm font-semibold capitalize text-white">
                        {c.labels.calendar.dayTimes} {selectedDay.label}
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {selectedDay.slots.map((slot) => {
                          const active = selected && selected.id === slot.id
                          return (
                            <button
                              key={slot.id}
                              type="button"
                              onClick={() => {
                                setSelected(slot)
                                trackEvent('funnel_booking_slot', { start: slot.start })
                              }}
                              aria-pressed={active}
                              className={`rounded-md border px-4 py-2 text-[14px] font-semibold transition-all duration-200 ${
                                active
                                  ? 'border-pyramid-19 bg-pyramid-19 text-[#101418]'
                                  : 'border-pyramid-19/40 text-pyramid-19 hover:bg-pyramid-19/10'
                              }`}
                            >
                              {slot.timeLabel}
                            </button>
                          )
                        })}
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-slate-400">{c.labels.calendar.pickDay}</p>
                  )}
                </div>
              </section>

              <form onSubmit={submit} noValidate className="rounded-2xl border border-white/10 bg-[#151b23] p-6 md:p-8">
                <label htmlFor="bk-name" className="text-xs uppercase tracking-[0.3em] text-slate-400">{c.labels.form.name}</label>
                <input id="bk-name" type="text" value={name} onChange={(e) => setName(e.target.value)}
                  className="mt-2 w-full rounded-lg border border-white/[0.12] bg-[#11161c] px-4 py-3 text-[15px] text-white focus:border-pyramid-19/60 focus:outline-none focus:ring-2 focus:ring-pyramid-19/20" />
                <label htmlFor="bk-email" className="mt-5 block text-xs uppercase tracking-[0.3em] text-slate-400">{c.labels.form.email}</label>
                <input id="bk-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="mt-2 w-full rounded-lg border border-white/[0.12] bg-[#11161c] px-4 py-3 text-[15px] text-white focus:border-pyramid-19/60 focus:outline-none focus:ring-2 focus:ring-pyramid-19/20" />
                <label htmlFor="bk-company" className="mt-5 block text-xs uppercase tracking-[0.3em] text-slate-400">{c.labels.form.company}</label>
                <input id="bk-company" type="text" value={company} onChange={(e) => setCompany(e.target.value)}
                  className="mt-2 w-full rounded-lg border border-white/[0.12] bg-[#11161c] px-4 py-3 text-[15px] text-white focus:border-pyramid-19/60 focus:outline-none focus:ring-2 focus:ring-pyramid-19/20" />
                <label htmlFor="bk-notes" className="mt-5 block text-xs uppercase tracking-[0.3em] text-slate-400">{c.labels.form.notes}</label>
                <textarea id="bk-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                  className="mt-2 w-full rounded-lg border border-white/[0.12] bg-[#11161c] px-4 py-3 text-[15px] text-white focus:border-pyramid-19/60 focus:outline-none focus:ring-2 focus:ring-pyramid-19/20" />
                {selected ? (
                  <p className="mt-4 text-sm text-slate-300">
                    <span className="font-semibold text-pyramid-19">{days.find((d) => d.slots.some((s) => s.id === selected.id)) && days.find((d) => d.slots.some((s) => s.id === selected.id)).label}</span>
                    <span className="mx-2" aria-hidden="true">&middot;</span>
                    {selected.timeLabel}
                  </p>
                ) : null}
                {error ? (
                  <p role="alert" className="mt-4 rounded-lg border border-pyramid-10/40 bg-pyramid-10/10 px-4 py-3 text-sm text-pyramid-10">{error}</p>
                ) : null}
                <button type="submit" disabled={sending || !selected} className="btn-board btn-board-cyan mt-6 disabled:cursor-not-allowed disabled:opacity-50">
                  {sending ? c.labels.form.sending : c.labels.form.submit}
                  <span aria-hidden="true">&#8594;</span>
                </button>
              </form>
            </div>
          ) : null}

          {phase === 'done' && confirmation ? (
            <div className="pane-swap is-visible max-w-xl rounded-2xl border border-pyramid-1/40 bg-pyramid-1/[0.06] p-8">
              <p className="text-xs uppercase tracking-[0.3em] text-pyramid-1">{c.labels.done.title}</p>
              <p className="mt-4 text-2xl font-light text-white">{confirmedWhen}</p>
              <p className="mt-3 leading-7 text-slate-300">{c.labels.done.body}</p>
              <div className="mt-6 flex flex-wrap gap-4">
                {confirmation.meetLink ? (
                  <a href={confirmation.meetLink} target="_blank" rel="noopener noreferrer" className="btn-board btn-board-lime">
                    {c.labels.done.meet}
                    <span aria-hidden="true">&#8594;</span>
                  </a>
                ) : null}
                <a href="/talk" className="inline-flex items-center gap-2 self-center text-sm font-semibold text-pyramid-19">
                  {c.labels.done.talk}
                  <span aria-hidden="true">&#8594;</span>
                </a>
              </div>
            </div>
          ) : null}
        </div>
      </main>
    </div>
  )
}

export default BookingPage
